<app-question-base [question]="question" [isFirstQuestion]="isFirstQuestion">
  <div
    #container
    class="flex gap-4 flex-col md:flex-row"
    (focusin)="onFocusIn()"
    (focusout)="onFocusOut()"
  >
    <div
      role="region"
      class="p-4 md:w-1/2"
      data-container="available"
      [attr.aria-label]="'question.prioritydragdrop.availableitems' | translate"
      (dragover)="$event.preventDefault()"
      (drop)="onDrop($event, 'available')"
    >
      <div class="flex justify-between items-center mb-4">
        <p class="font-semibold" id="available-items-title" aria-hidden="true">
          {{ "question.prioritydragdrop.availableitems" | translate }}
        </p>
      </div>

      <ul
        [attr.aria-labelledby]="'available-items-title'"
        class="list-none border-[1.5px] min-h-[3.75rem] p-5"
      >
        @for (item of availableItems; track $index) {
          <li
            [attr.data-item-id]="item.id"
            [attr.aria-grabbed]="isDragging === item"
            class="transition-transform-slow lg:hover:scale-105 item-color p-3 mb-1 text-base bg-inherit hover:bg-[rgba(0,0,0,0.1)] flex items-center min-h-[3rem]"
            [ngClass]="{
              'item-disabled': maxSelected,
              'item-spacing': $index !== availableItems.length - 1,
            }"
            [class.border-blue-500]="draggedOverItem === item"
            [attr.aria-disabled]="maxSelected"
          >
            <app-button
              (buttonClick)="moveToSelected(item)"
              buttonId="select-button-{{ question.id }}-{{ $index }}"
              [buttonLabel]="
                'question.prioritydragdrop.selectlabel'
                  | translate: { title: item.title }
              "
              [icon]="{
                iconType: Icon.PlusCircle,
                iconColor: 'inherit',
                iconHeight: 25,
                iconWidth: 25,
              }"
              [disabled]="maxSelected"
            ></app-button>
            <span
              class="ml-2 cursor-move flex-1 break-words min-w-0"
              draggable="true"
              tabindex="-1"
              role="presentation"
              (dragstart)="onDragStart(item, 'available')"
              (dragover)="onDragOver($event, item)"
              (dragleave)="onDragLeave()"
              (dragend)="onDragEnd()"
              (drop)="onDrop($event, 'available', item)"
              (touchstart)="onTouchStart($event, item, 'available')"
              (touchmove)="onTouchMove($event)"
              (touchend)="onTouchEnd($event)"
              >{{ item.title }}</span
            >
          </li>
        }
      </ul>
    </div>

    <div
      class="p-4 md:w-1/2"
      data-container="selected"
      [attr.aria-label]="'question.prioritydragdrop.selecteditems' | translate"
      role="region"
      (dragover)="$event.preventDefault()"
      (drop)="onDrop($event, 'selected')"
    >
      <p
        class="mb-4 font-semibold"
        id="selected-items-title"
        aria-hidden="true"
      >
        {{ "question.prioritydragdrop.selecteditems" | translate }}
      </p>
      <ol
        [attr.aria-labelledby]="'selected-items-title'"
        class="list-none border-[1.5px] border-black min-h-[3.75rem] p-5"
      >
        @for (item of control.value; track $index) {
          <li
            [attr.data-item-id]="item.id"
            [attr.aria-grabbed]="isDragging === item"
            [attr.aria-posinset]="$index + 1"
            [attr.aria-setsize]="control.value.length"
            class="transition-transform-slow lg:hover:scale-105 p-3 text-base bg-inherit hover:bg-[rgba(0,0,0,0.1)] flex items-center min-h-[3rem]"
            [class.border-blue-500]="draggedOverItem === item"
            [ngClass]="{ 'item-spacing': $index !== control.value.length - 1 }"
          >
            <app-button
              customClasses="!p-[0.25rem] cross-icon"
              (buttonClick)="unselectItem(item)"
              buttonId="unselect-button-{{ question.id }}-{{ $index }}"
              [buttonLabel]="
                'question.prioritydragdrop.unselectlabel'
                  | translate: { title: item.title }
              "
              [icon]="{
                iconType: Icon.Cross,
                iconColor: 'currentColor',
                iconHeight: 25,
                iconWidth: 25,
              }"
            ></app-button>

            <span
              class="ml-2 cursor-move flex-1 break-words min-w-0"
              draggable="true"
              tabindex="-1"
              role="presentation"
              (dragstart)="onDragStart(item, 'selected')"
              (dragover)="onDragOver($event, item)"
              (dragleave)="onDragLeave()"
              (dragend)="onDragEnd()"
              (drop)="onDrop($event, 'selected', item)"
              (touchstart)="onTouchStart($event, item, 'selected')"
              (touchmove)="onTouchMove($event)"
              (touchend)="onTouchEnd($event)"
              >{{ item.title }}</span
            >
            <div class="flex pl-1 md:pl-2 gap-2">
              <div class="w-[21px]">
                @if ($index !== 0) {
                  <app-button
                    customClasses="!p-0"
                    (buttonClick)="moveUp(item)"
                    [buttonLabel]="
                      'question.prioritydragdrop.prioup'
                        | translate: { title: item.title }
                    "
                    [icon]="{
                      iconType: Icon.CircleArrow,
                      iconColor: 'inherit',
                      iconHeight: 21,
                      iconRotation: 180,
                      iconWidth: 21,
                    }"
                    buttonId="{{
                      'prio-up-button-' + question.id + '-' + $index
                    }}"
                  ></app-button>
                }
              </div>
              <div class="w-[21px]">
                @if ($index !== control.value.length - 1) {
                  <app-button
                    customClasses="!p-0"
                    (buttonClick)="moveDown(item)"
                    [buttonLabel]="
                      'question.prioritydragdrop.priodown'
                        | translate: { title: item.title }
                    "
                    [icon]="{
                      iconType: Icon.CircleArrow,
                      iconColor: 'inherit',
                      iconHeight: 21,
                      iconWidth: 21,
                    }"
                    buttonId="{{
                      'prio-down-button-' + question.id + '-' + $index
                    }}"
                  ></app-button>
                }
              </div>
            </div>
          </li>
        }
      </ol>
    </div>
  </div>
  @if (maxSelected) {
    <app-validator
      [question]="question"
      [validator]="ValidatorType.MaxSelections"
    ></app-validator>
  }
  @if (control.touched && control.invalid) {
    <app-validator
      [question]="question"
      [control]="control"
      [validator]="ValidatorType.RequiredSelect"
    ></app-validator>
  }
</app-question-base>
